import {
  CloudDownload as CloudDownloadIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { QueryTags } from "../consts";
import {
  useBackgroundSync,
  useBlockingDataSyncThread as useBlockingDataSync,
} from "../hooks/data-sync.hooks";
import {
  ServiceWorkerAppActions,
  ServiceWorkerAppStateKeys,
} from "../libs/workers/consts";
import { usePWA } from "../providers";

export function ServiceWorkerAppClientProvider({
  initialSyncDelayMs,
  children,
}: {
  initialSyncDelayMs?: number;
  children?: React.ReactNode;
}) {
  const backgroundDataSync = useBackgroundSync();
  const blockingDataSync = useBlockingDataSync();
  const queryClient = useQueryClient();

  const pwa = usePWA();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (pwa.status.isPWASupported == null) {
      console.log("PWA status unknown, not parsing new events");
      return;
    }

    setTimeout(() => {
      parseNewEvents();
    }, initialSyncDelayMs ?? 0);
  }, [pwa.status.isPWASupported]);

  useEffect(() => {
    if (pwa.status.isPWASupported == null) {
      console.log("PWA status unknown, not setting up service worker listener");
      return;
    }

    const abortController = new AbortController();
    if (pwa.status.isPWASupported) {
      console.log("PWA supported, setting up service worker listener");
      navigator.serviceWorker.addEventListener(
        "message",
        (event) => {
          if (event.data?.type === ServiceWorkerAppStateKeys.READY) {
            console.log("READY:", event.data);
            setReady(true);
          } else if (
            event.data?.type === ServiceWorkerAppStateKeys.SYNC_COMPLETE
          ) {
            setReady(true);
          } else if (event.data?.type === ServiceWorkerAppStateKeys.NOT_READY) {
            navigator.serviceWorker.getRegistration().then((reg) => {
              reg?.active?.postMessage({
                type: ServiceWorkerAppActions.INITIALIZE,
              });
            });
            setReady(false);
          } else if (event.data?.type === ServiceWorkerAppStateKeys.LOADED) {
            console.log("WORKER_LOADED message received");
            queryClient
              .invalidateQueries({
                queryKey: [QueryTags.SERVICE_WORKER_BASED],
                predicate(query) {
                  return !Boolean(query.state.data);
                },
              })
              .then(() => {
                console.log(
                  "Service worker loaded at",
                  event.data?.data?.timestamp
                );
              });
          } else if (event.data?.type === "ERROR") {
            console.log(
              "Error message received from service worker:",
              event.data
            );
            if (event.data?.data?.request?.type) {
              queryClient.invalidateQueries({
                queryKey: [
                  QueryTags.SERVICE_WORKER_BASED,
                  event.data.data.request.type,
                ],
              });
            }
          }
        },
        { signal: abortController.signal }
      );

      navigator.serviceWorker.getRegistration().then((reg) => {
        reg?.active?.postMessage({
          type: ServiceWorkerAppActions.CHECK_READY,
          opts: { verbose: false },
        });
      });

      // navigator.serviceWorker.controller?.postMessage({ type: "CHECK_READY" });
    } else {
      console.log(
        "PWA not supported, setting ready to true"
      );
      setReady(true);
    }
    return () => {
      abortController.abort();
    };
  }, [pwa.status.isPWASupported]);

  const parseNewEvents = () => {
    if (pwa.status.isPWASupported == null) {
      console.log("PWA status unknown, not parsing new events");
      return;
    } else if (
      pwa.status.isPWASupported &&
      (backgroundDataSync.status === "idle" || error)
    ) {
      backgroundDataSync.mutate();
    } else if (
      !pwa.status.isPWASupported &&
      (blockingDataSync.status === "idle" || error)
    ) {
      blockingDataSync.mutate();
    }
  };

  const isSyncingInMainThread =
    !pwa.status.isPWASupported &&
    ["loading", "pending", "success"].includes(blockingDataSync.status);

  const isSyncingInBackground =
    pwa.status.isPWASupported &&
    ["loading", "pending", "success"].includes(backgroundDataSync.status);

  const error = backgroundDataSync.error || blockingDataSync.error;

  if (!ready) {
    if (isSyncingInBackground) {
      return <BackgroundDataLoading />;
    } else if (isSyncingInMainThread) {
      return <SynchronousDataLoading msg="Sincronizzazione in corso..." />;
    } else {
      return <SynchronousDataLoading msg="Inizializzazione in corso..." />;
    }
  }
  //  else if (error) {
  //   return <DataLoadingError error={error?.message} onRetry={parseNewEvents} />;
  // }

  return children;
}

export function SynchronousDataLoading({
  msg = "Caricamento in corso...",
}: {
  msg?: string;
}) {
  return (
    <Container maxWidth="sm">
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Stack
          justifyContent="center"
          spacing={2}
          textAlign="center"
          alignItems={"center"}
        >
          <CircularProgress size={48} sx={{ mb: 3 }} />
          <Typography variant="h6" color="text.secondary">
            {msg}
          </Typography>
        </Stack>
      </Box>
    </Container>
  );
}

export function BackgroundDataLoading() {
  return (
    <Container maxWidth="sm">
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Card elevation={3}>
          <CardContent sx={{ textAlign: "center", p: 4 }}>
            <Box mb={3}>
              <img
                src="/logo.png"
                alt="Analytics"
                style={{ height: 64, width: 64, marginBottom: 16 }}
              />
              <Typography variant="h4" gutterBottom>
                Preparazione Analisi
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Stiamo scaricando e preparando tutti i dati per l'analisi.
                <br />
                Riceverai una notifica quando tutto sarà pronto.
              </Typography>
            </Box>

            <Stack spacing={2}>
              <Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography variant="body2" color="text.secondary">
                    Download matches e eventi...
                  </Typography>
                  <CloudDownloadIcon color="primary" />
                </Box>
                <LinearProgress
                  variant="indeterminate"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              <Alert severity="info" variant="outlined">
                <Typography variant="caption">
                  Questo processo avviene in background. Puoi chiudere questa
                  finestra.
                </Typography>
              </Alert>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}

export function DataLoadingError({
  error,
  onRetry,
}: {
  error: string;
  onRetry: () => void;
}) {
  return (
    <Container maxWidth="sm">
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Card elevation={3}>
          <CardContent sx={{ textAlign: "center", p: 4 }}>
            <WarningIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h4" color="error" gutterBottom>
              Errore di Sincronizzazione
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Button variant="contained" onClick={onRetry} size="large">
              Riprova
            </Button>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
